import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import useScrollToTop from "@/hooks/useScrollToTop";
import { Helmet } from "react-helmet-async";

const AntiFraudPolicy = () => {
  useScrollToTop();

  useEffect(() => {
    document.title = "Anti-Fraud Policy | YouAux";
  }, []);

  return (
    <>
    <Helmet>
        <title>Anti-Fraud Policy | YouAux</title>
        <meta name="description" content="This Anti-Fraud Policy sets a course of action for the End Users of our platform and services, and defines the actions taken and consequences of Fraudulent behaviour." />
        <link rel="canonical" href="https://youaux.com/anti-fraud-policy" />
      </Helmet>
      <Navbar />
      <main className="ophira-bg bg-ophira-dark min-h-screen">
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto bg-ophira-darkgray/30 p-8 rounded-lg shadow-xl">
            <div className="mb-10 text-center">
              <span className="text-ophira-yellow text-sm font-medium uppercase tracking-wider">
                EFFECTIVE AS OF APRIL 15, 2025
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mt-4 mb-8">ANTI-FRAUD POLICY</h1>
              <p className="text-white/50 text-xl">For End-Users</p>
            </div>

            <div className="prose prose-invert prose-lg max-w-none">
              {/* <h2 className="text-2xl font-bold mt-10 mb-6">Table of Contents:</h2>
              <ul className="text-ophira-lightgray ml-6">
                <li>Introduction</li>
                <li>Definitions</li>
                <li>Policy</li>
                <li>What DSPs are doing about it</li>
                <li>What we are doing about it</li>
                <li>How we deal with suspected Fraud</li>
                <li>The consequences of Fraudulent use of our Platform or Services</li>
                <li>Severity, Strike Policy and Account Blocking</li>
                <li>Takedowns</li>
              </ul> */}

              <h2 className="text-2xl font-bold mt-10 mb-6">Introduction</h2>
              <p className="text-white/50">
                For the purpose of this Policy, the <span className="font-medium">"End User"</span> will be the person or legal entity that has entered into a contractual relationship with Us, accepting the Terms of Use and providing content that will be made available on Digital Streaming Platforms (or DSP). <span className="font-medium">"We"</span> or <span className="font-medium">"Us"</span> are the providers of the service, <span className="text-ophira-yellow"><Link to="/" className="text-ophira-yellow">YOUAUX ENTERTAINMENT</Link></span>.
              </p>

              <p className="text-white/50 mt-6">
                This Anti-Fraud Policy sets a course of action for the End Users of our platform and services, and defines the actions taken and consequences of Fraudulent behaviour.
              </p>

              <p className="text-white/50 mt-6">
                Specifically, the purpose of this Policy is to provide:
              </p>

              <ul className="text-white/50 ml-6 mt-4 list-disc">
                <li>A clear definition of what we mean by "Fraud".</li>
                <li>A definitive statement to take the distance of fraudulent activity in all its forms.</li>
                <li>A summary to the End User's responsibilities in avoiding fraudulent activities.</li>
                <li>Guidance to all parties involved as to action which should be taken when we, the DSP or any other third party suspect any fraudulent activity.</li>
                <li>Clear guidance as to responsibilities for conducting investigations into fraud-related activities.</li>
                <li>Protection to right holders in circumstances where they may be victimized as a consequence of fraudulent activities.</li>
              </ul>

              <p className="text-white/50 mt-6">
                This document is intended to provide guidance and should be read in conjunction with:
              </p>

              <ul className="text-white/50 ml-6 mt-4 list-disc">
                <li>The <Link to="/privacy" className="text-ophira-yellow">Privacy Policy</Link></li>
                <li>The <Link to="/terms-and-conditions" className="text-ophira-yellow">Terms of Use</Link></li>
              </ul>

              <h2 className="text-2xl font-bold mt-10 mb-6">Definitions</h2>

              <div className="ml-6 mt-4">
                <p className="text-white/50 mb-4">
                  <span className="font-medium">End User:</span> Is the person or legal entity that has entered into a contractual relationship with YOUAUX ENTERTAINMENT for the use of the platform, accepting the Terms of Use and providing content that will be made available on DSP.
                </p>

                <p className="text-white/50 mb-4">
                  <span className="font-medium">End User Account:</span> Accounts owned and/or created by End Users.
                </p>

                <p className="text-white/50 mb-4">
                  <span className="font-medium">DSP:</span> Digital Streaming Platforms (such as Spotify, Apple Music, Tidal, etc.) or any other channel connected to Us by a contractual relationship.
                </p>

                <p className="text-white/50 mb-4">
                  <span className="font-medium">MDFS:</span> Monetization through Digital Fingerprinting Systems (i.e. YouTube Content ID, Facebook Rights Manager, among others).
                </p>

                <p className="text-white/50 mb-4">
                  <span className="font-medium">Fraud:</span> Any activity contrary to the Law, the DSP Policies and/or our own. Particularly, but non-exclusively, we will categorize as Fraud the following activities:
                </p>

                <ul className="text-white/50 ml-6 mb-4 list-disc">
                  <li>The unauthorized exploitation of material protected by Copyright (Copyright Infringement).</li>
                  <li>The infringement of any Law that regulates the ownership of legal right holders.</li>
                  <li>The use of (misleading) artist, band or label names or artwork which may create confusion or false expectations related to content which is served to consumers by DSP (i.e., Musical Spam and Misleading Content).</li>
                  <li>The use of automated digital bots or other means to "click" on payment-generating links pretending to be consumers (i.e., Click Fraud), producing unnatural and fraudulent incomes.</li>
                  <li>The upload of distorted music, silenced songs, or with any other deceptive features, also to generate revenues and other unauthorized activities which are in breach with the Terms of Use and/or the agreements we have with DSP.</li>
                  <li>The use of manual or automated resources, bots or any other means with the intention to stream and monetize DSP content in violation with DSP terms and conditions.</li>
                  <li>The use of manual or automated resources, bots or any other means to unlawfully monetise content through MDFS.</li>
                </ul>

                <p className="text-white/50 mb-4">
                  <span className="font-medium">Royalties:</span> Economic income/revenue that corresponds to the original(s) rightsholder(s). We differentiate between "legitimate royalties" which are the result of an actual and rightful exploitation of any content, and "illegitimate royalties" which are the result of any fraudulent exploitation or activity that generates illicit and unrightful income.
                </p>

                <p className="text-white/50 mb-4">
                  <span className="font-medium">Strike:</span> A Strike is a severity applied to any account that violates our Anti-Fraud Policy. They are separated into three tiers (Strike 1, 2 and 3), from lowest to highest severity respectively, and applied cumulatively. However, according to the impact of the violation, the highest severity shall be directly applied.
                </p>
              </div>

              <h2 className="text-2xl font-bold mt-10 mb-6">Policy</h2>

              <p className="text-white/50">
                Fraud in all its forms is wrong and is unacceptable to us. This is because when fraud occurs:
              </p>

              <ul className="text-white/50 ml-6 mt-4 list-disc">
                <li>It is not just a loss to us and the DSP, but ultimately to other content creators, damaging their copyrights, reputation or reducing the royalty pool which is made available by the DSP to content creators.</li>
                <li>It may have a major impact on our reputation, our contracts with DSP and therefore again on other End Users using our Services.</li>
              </ul>

              <p className="text-white/50 mt-6">
                Our objective is to avoid and, if necessary, eliminate fraudulent usage of our Services. Any indications of fraud will be rigorously investigated and dealt with in a firm and controlled way.
              </p>

              <h2 className="text-2xl font-bold mt-10 mb-6">What DSPs are doing about it</h2>

              <p className="text-white/50">
                Most DSP use a mix of algorithms and human resources to scan their catalog to avoid potential fraud or unauthorized and artificial streams and abusive use of their service. Once they have identified fraudulent activity they take down the content and inform us about the case, reserving the right to withhold from future payments the amounts previously generated through suspicious activities.
              </p>

              <h2 className="text-2xl font-bold mt-10 mb-6">What we are doing about it</h2>

              <p className="text-white/50">
                We are actively taking action in the following fields:
              </p>

              <ul className="text-white/50 ml-6 mt-4 list-disc">
                <li>We are automatically monitoring (historic) sales data to combine this with other information (i.e., artist profiles, End User information, social media, etc.), to detect possible irregular activity.</li>
                <li>Our entire back catalog and all new tracks are fingerprinted and matched against several databases to avoid multiple uploads of the same song, uploads of "white noise", "empty songs", "copyrighted materials" and, in general, any unauthorized activity.</li>
                <li>Our Quality Control (QC) processes are designed to track down the use of metadata that could potentially result in Musical Spam and Misleading Content or any other type of fraudulent activity.</li>
              </ul>

              <h2 className="text-2xl font-bold mt-10 mb-6">How we deal with suspected Fraud</h2>

              <ul className="text-white/50 ml-6 mt-4 list-disc">
                <li>In case we detect or have suspicions of any unauthorized activity (including bot-generated streams, click fraud, music spam, abusive usage of MDFS, etc.) from a specific End User Account, we will warn the End User via our Strike Policy System, which may ultimately result in blocking of the involved End User Account.</li>
                <li>Revenues received in connection with content that we believe, in our sole discretion, violate the Terms of Use, can be blocked and retained.</li>
              </ul>

              <h2 className="text-2xl font-bold mt-10 mb-6">The consequences of Fraudulent use of our Platform or Services</h2>

              <ul className="text-white/50 ml-6 mt-4 list-disc">
                <li>If an End User is deemed breaching the Terms of Use, we will have the right to terminate the contractual relationship.</li>
                <li>Any amounts due to an End User from any DSP derived from any fraudulent or unauthorized use of the service may be recouped by withholding such amounts from future payments due to that same End User.</li>
                <li>To the extent any fraudulent and/or infringing activities are determined to be caused by the End User's actions, the End User may be held responsible for any costs incurred by us or our providers (including legal fees and expenses) in connection therewith. These costs, in addition to other remedies, may be deducted by Us from any future payments due to an End User.</li>
              </ul>

              <h2 className="text-2xl font-bold mt-10 mb-6">Severity, Strike Policy and Blocking of Accounts</h2>

              <h3 className="text-xl font-bold mt-8 mb-4">Severity</h3>

              <p className="text-white/50">
                Either during our QC processes, the sales confirmation process or through notifications received from DSP and/or third parties, we may detect issues of possible fraudulent content or Accounts.
              </p>

              <p className="text-white/50 mt-4">
                Once possible fraudulent content or Accounts are detected, a strike will be applied and the End User Account may be blocked in the following cases, divided into 4 severity tiers, which we consider very severe:
              </p>

              <ul className="text-white/50 ml-6 mt-4 list-disc">
                <li><span className="font-medium">F0:</span> Critical issues related to an End User Account</li>
                <li><span className="font-medium">F1:</span> Issues related to Click Fraud, Misleading, Musical Spam and/or Artificial Streams</li>
                <li><span className="font-medium">F2:</span> Issues related to Copyright, Intellectual Property or Trademark</li>
                <li><span className="font-medium">F3:</span> Issues related to Abusive Usage of MDFS</li>
              </ul>

              <p className="text-white/50 mt-6 font-medium">F0: Critical issues related to an End User Account</p>

              <p className="text-white/50 mt-2">
                Particularly, but non-exclusively, we will categorize as F0 the following:
              </p>

              <ul className="text-white/50 ml-6 mt-4 list-disc">
                <li>An End User Account profile contains fake or deliberately incomplete information or unauthorized content and/or information that belongs to a third party.</li>
                <li>Repeated, inconsistent IP activity versus declared country or address of origin on any given End User Account.</li>
                <li>End Users, when required to present documents of any kind, send falsifications and/or deceptive, suspicious documents.</li>
                <li>Any other signs that any End User is deliberately trying to avoid a correct identification or validation of identity or address.</li>
                <li>Any End User account generating royalties without having any detailed profile information in "My account" page.</li>
              </ul>

              <p className="text-white/50 mt-6 font-medium">F1: Critical issues related to Click Fraud, Misleading, Musical Spam and/or Artificial Streams</p>

              <p className="text-white/50 mt-2">
                Particularly, but non-exclusively, we will categorize as F1 the following activities:
              </p>

              <ul className="text-white/50 ml-6 mt-4 list-disc">
                <li>An Account contains releases from unknown artists which are generating a considerable number of streams (Click Fraud) or views and subsequently, revenues in a short period of time without a minimum, credible fanbase to support it.</li>
                <li>Any sudden, suspicious uplift in sales without corresponding numbers in profile views or social media activity to support it.</li>
                <li>Any signs of intention to take advantage of well known artists' names, song titles or other copyrighted audiovisual content to attract consumers to click (Misleading).</li>
                <li>Hiring a Streaming/Followers Growth Service in order to generate artificial, unnatural, revenue and popularity.</li>
              </ul>

              <p className="text-white/50 mt-6 font-medium">F2: Issues related to Copyright, Intellectual Property or Trademark</p>

              <p className="text-white/50 mt-2">
                Particularly, but non-exclusively, we will categorize as F2 the following activities:
              </p>

              <ul className="text-white/50 ml-6 mt-4 list-disc">
                <li>Whenever an impersonation of any artist, recording company, unauthorized use of trademarks, distinctive signs, song or album titles, etc., is confirmed.</li>
                <li>A release contains copyrighted material from a Rightsholder who did not grant publishing permission to the End User.</li>
                <li>We received a content infringement notification from a DSP and/or any third party in relation to a content that has been sent to DSP.</li>
                <li>We receive a direct infringement claim from the original Rightsholder or his/her representative.</li>
              </ul>

              <p className="text-white/50 mt-6 font-medium">F3: Issues related to abusive usage of MDFS</p>

              <p className="text-white/50 mt-2">
                Particularly, but non-exclusively, we will categorize as F3 the following activities:
              </p>

              <ul className="text-white/50 ml-6 mt-4 list-disc">
                <li>Insert of an own original content within or among a third party's original copyrighted content, with the intention of taking advantage of such content's popularity, in order to generate higher income.</li>
                <li>Artificial Streams/Plays applied to abusive usage of MDFS cases (i.e. the use of bots to generate false plays of a monetized content).</li>
                <li>Any sudden, suspicious uplift in sales related to MDFS, without proper historical consistency or plausible reason.</li>
              </ul>

              <p className="text-white/50 mt-4">
                In case we detect one or more of these issues in the content of an End User Account, the End User will receive a ticket or notice, indicating we have detected potential infringing or unauthorized activity and informing the End User about its consequences, described in our Strike Policy below.
              </p>

              <h3 className="text-xl font-bold mt-8 mb-4">Strike Policy</h3>

              <p className="text-white/50">
                In case We detect an F1, F2 and/or F3 issues in an End User Account, a strike is applied to the Account and additional actions from the End User may be required, such as:
              </p>

              <ul className="text-white/50 ml-6 mt-4 list-disc">
                <li>Completing the information details in their "My Account" page.</li>
                <li>Sending Us a copy of an identification document (passport or national ID).</li>
                <li>In most cases, we also request artist profiles, website URLs, social media profiles, as well as any information from the artist in order to contrast this with the sales data.</li>
              </ul>

              <p className="text-white/50 mt-4">
                In case a strike is applied and the End User: a. deliberately ignores Our notice, b. refuses or cannot provide the requested information within 5 working days (counting from the day the notice is sent), c. the infringing or unauthorized activity is confirmed, a Strike will be applied to the End User Account, which will have the following consequences:
              </p>

              <div className="bg-ophira-darkgray/50 p-6 mt-6 rounded-lg">
                <p className="text-ophira-yellow font-bold mb-2">STRIKE 1</p>
                <ul className="text-white/50 ml-6 list-disc">
                  <li>End User is informed.</li>
                  <li>Takedown of questioned content.</li>
                  <li>Warning of royalty payment delay on 2nd strike and block on 3rd strike.</li>
                </ul>
              </div>

              <div className="bg-ophira-darkgray/50 p-6 mt-4 rounded-lg">
                <p className="text-ophira-yellow font-bold mb-2">STRIKE 2</p>
                <ul className="text-white/50 ml-6 list-disc">
                  <li>End User is informed.</li>
                  <li>Takedown of questioned content.</li>
                  <li>Royalty payment delay: Any Royalty outpayment request will be processed with a delay of 3 months counting from the moment an outpayment is requested.</li>
                  <li>Warning that the account will be blocked and royalties will be kept in escrow if a 3rd strike is applied.</li>
                </ul>
              </div>

              <div className="bg-ophira-darkgray/50 p-6 mt-4 rounded-lg">
                <p className="text-ophira-yellow font-bold mb-2">STRIKE 3</p>
                <ul className="text-white/50 ml-6 list-disc">
                  <li>End User is informed.</li>
                  <li>End User account is blocked.</li>
                  <li>The entire End User's catalog will be taken down.</li>
                  <li>Legitimate royalties will be kept in escrow for a minimum of 24 months, and up to 5 years, in accordance with DSPs policies and the Spanish Civil Law. Illegitimate royalties will be returned to DSP.</li>
                </ul>
              </div>

              <h3 className="text-xl font-bold mt-8 mb-4">Blocked Account Policy</h3>

              <p className="text-white/50">
                In case an End User repeatedly violates our Terms of Use, this Anti-Fraud Policy and/or the agreements we have with DSP, we can proceed to block the End User Account.
              </p>

              <p className="text-white/50 mt-4">
                This will have the following implications:
              </p>

              <ul className="text-white/50 ml-6 mt-4 list-disc">
                <li>We may discontinue the contractual relationship with the End User.</li>
                <li>The entire End User's catalog will be taken down.</li>
                <li>Blocked Accounts will not be able to access the platform and, therefore, enjoy our services.</li>
                <li>Legitimate royalties are kept in escrow for a minimum of 24 months, and up to 5 years, in accordance with DSPs policies and the Spanish Civil Law, or until the dispute between claimant and claimed parties is solved.</li>
                <li>In a context of a dispute, the disputing parties shall inform us about the result of the dispute, so we can determine what amounts should be returned to them, including incurred expenses or economic claims, penalties or compensations determined by Law. Once this process has concluded, and the escrow period has passed, any remaining funds will be released to be transferred to the End User (if claimed).</li>
              </ul>

              <h3 className="text-xl font-bold mt-8 mb-4">Withholding Royalties ("Escrow") for blocked accounts:</h3>

              <p className="text-white/50">
                Revenues in any End User Account that are received in connection with content that We believe, in our sole discretion, violate the Terms of Use may be blocked and withheld. The royalties will be held in escrow for a minimum of 24 months, and up to 5 years, in accordance with DSPs policies and the Spanish Civil Law or until the dispute between claimant and claimed parties is solved, in order to be able to respond in the following scenarios:
              </p>

              <ul className="text-white/50 ml-6 mt-4 list-disc">
                <li><span className="font-medium">Illegitimate Royalties Refund:</span> A claim from DSP is received, asking for a refund of the royalties in case any DSP deems the Royalties were generated through unauthorized or fraudulent activity. Please note that DSP contractually have the right to reclaim royalties during a timeframe of minimum 24 months, that is why we need to respect this minimum period as well.</li>
                <li><span className="font-medium">Legitimate Royalties Refund:</span> A claim from the legitimate rights holder is received, claiming the payout of the royalties that have been retained.</li>
              </ul>

              <h2 className="text-2xl font-bold mt-10 mb-6">Spotify Fake Streaming & Fraudulent Activity Policy</h2>
              <p className="text-white/50">
              At YouAux, we maintain a strict zero-tolerance policy towards any form of fraudulent streaming activity. This includes, but is not limited to, fake streaming, bot-generated streaming, stream manipulation, or the purchase of artificial plays, likes, or followers through any third-party services or websites.
              </p>

              <p className="text-white/50 mt-4">
                If our Anti-Fraud Team or any official Spotify audit or notice identifies that your content has been involved in such activities, your royalty payments and payouts will be immediately withheld pending further investigation. Such actions are considered a serious violation of Spotify’s Terms & Conditions and may negatively affect our partnership and standing with Spotify. In confirmed cases of fraudulent activity, a financial penalty of €10 (Euro) per track will be imposed. If you fail to settle the penalty amount within the specified timeframe, it will be automatically deducted from your upcoming royalty or distribution payments. Repeated violations may lead to permanent suspension or termination of your distribution account without any further notice.
              </p>

              <p className="text-white/50 mt-4">
                Please note that we only support authentic, organic, and legitimate streaming activity. Any attempt to artificially inflate stream counts, manipulate algorithms, or gain unfair monetary advantage will be treated as intentional fraud and may also expose you to further legal and financial liabilities. By using our platform and distributing your music through us, you agree to comply fully with this policy and to maintain the integrity of your streaming data across all digital service providers. Any attempt to manipulate streaming data may also result in account suspension or permanent termination.
              </p>


              <h2 className="text-2xl font-bold mt-10 mb-6">Copyright Conflict & Compliance Policy</h2>

              <p className="text-white/50">
                In the event that our team receives a copyright conflict or ownership dispute from any DSP (Digital Service Provider) or rights holder, the concerned user will be contacted by our compliance team for clarification. The user will be provided with a reasonable timeframe to submit valid ownership proof or supporting documents.
              </p>

              <p className="text-white/50 mt-4">
                Failure to respond or provide the required documentation within the given timeframe will be considered a violation of our platform policies. In such cases, the user’s account may be temporarily or permanently suspended. Additionally, any previously released content associated with the account will not be taken down automatically, the user will be required to pay applicable takedown fees, as the violation constitutes misuse of the platform and an attempt to commit fraud or misrepresentation against the company.
              </p>

              <p className="text-white/50 mt-4">
                Our company reserves the right to take appropriate action, including legal measures, to protect its operations, time, and reputation from fraudulent activities
              </p>

              <h2 className="text-2xl font-bold mt-10 mb-6">Takedowns</h2>

              <p className="text-white/50">
                We will initiate takedowns of confirmed infringing content from End User Accounts, that is, all content that is involved in F0, F1, F2 and/or F3 issues.
              </p>

              <p className="text-white/50 mt-4">
                Regarding F3 issues, despite the fact that only a part of the End User's content is confirmed to be fraud related, We, at our sole discretion, may initiate takedowns for the End User's entire catalog.
              </p>

              <p className="text-white/50 mt-4">
                However, please also note that any content may be marked as suspicious by DSP at their sole discretion and may be taken down.
              </p>
              <p className="text-white/50 mt-4">
              If a user wishes to request a takedown (removal) of a song from the YouAux platform, the following policies and procedures apply based on the user's selected distribution plan:

              <br /><br />

              <span className="font-medium">For Basic Plan Users:</span><br />
              Users on the Basic Plan are subject to a takedown fee for each song removal request. YouAux imposes this fee to cover administrative, operational, and distribution costs associated with processing the takedown. The user must contact YouAux support to initiate the takedown process, at which point payment of the applicable takedown fee will be required before the request is processed. 

              <br /><br />

              <span className="font-medium">Exemption Clause for Basic Plan:</span><br />
              If a Basic Plan user believes they have a valid and compelling reason for requesting the takedown (such as copyright issues, accidental uploads, or extenuating circumstances), they are encouraged to reach out to YouAux support and provide a detailed explanation and supporting documentation. Upon review, YouAux reserves the right, at its sole discretion, to waive (exempt) the takedown fee for Basic Plan users if the provided reason is deemed justified and in accordance with platform policies.

              <br /><br />

              <span className="font-medium">For Pro, Premium, and Premium+ Plan Users:</span><br />
              Users subscribed to Pro, Premium, or Premium+ Plans are entitled to complimentary takedowns. No takedown fee will be charged for song removals requested under these plans. Users can contact the support team or utilize any self-service features (if available) to request song removal without incurring additional charges.

              <br /><br />

              <span className="font-medium">General Notes:</span><br />
              All takedown requests, regardless of the user's plan, must be submitted through official support channels and in compliance with the platform's guidelines and terms of service. YouAux may request additional information to verify the ownership and legitimacy of takedown requests. Failure to provide accurate or sufficient information may result in delays or denial of the takedown request.
              </p>
            </div>
          </div>  
        </div>
      </main>
      <Footer />
    </>
  );
};

export default AntiFraudPolicy;
